import { useState, useEffect } from 'react';
import { X, Edit2, Trash2, Check, X as CancelIcon } from 'lucide-react';

interface ListItem {
  id: number;
  name: string;
}

interface ListManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'categories' | 'genres';
  apiUrl: string;
  token: string | null;
  onUpdate: () => void;
}

export default function ListManagerModal({ isOpen, onClose, type, apiUrl, token, onUpdate }: ListManagerModalProps) {
  const [items, setItems] = useState<ListItem[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');

  const endpoint = type === 'categories' ? 'content/categories' : 'content/genres';
  const title = type === 'categories' ? 'Manage Categories' : 'Manage Genres';

  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen]);

  const fetchItems = async () => {
    try {
      const res = await fetch(`${apiUrl}/${endpoint}`);
      if (res.ok) setItems(await res.json());
    } catch (err) {
      console.error(`Failed to fetch ${type}`, err);
    }
  };

  const startEdit = (item: ListItem) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = async (id: number) => {
    if (!editName.trim()) return;
    try {
      const res = await fetch(`${apiUrl}/${endpoint}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: editName })
      });
      if (res.ok) {
        setEditingId(null);
        fetchItems();
        onUpdate();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteItem = async (id: number) => {
    if (!confirm(`Are you sure you want to delete this ${type === 'categories' ? 'category' : 'genre'}?`)) return;
    try {
      const res = await fetch(`${apiUrl}/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchItems();
        onUpdate();
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No {type} found.</p>
          ) : (
            <ul className="space-y-3">
              {items.map(item => (
                <li key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                  {editingId === item.id ? (
                    <div className="flex items-center space-x-2 flex-1">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        autoFocus
                      />
                      <button onClick={() => saveEdit(item.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-md">
                        <Check size={16} />
                      </button>
                      <button onClick={cancelEdit} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md">
                        <CancelIcon size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="text-slate-700 font-medium">{item.name}</span>
                      <div className="flex items-center space-x-1">
                        <button onClick={() => startEdit(item)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => deleteItem(item.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
